/**
 * Open Questions Page — Harry Max Open Questions Framework
 *
 * Zero/one/few/many view pattern:
 *   zero → empty state
 *   one/few (1–7) → sortable card view (drag-and-drop)
 *   many (8+) → compact table view (default, with drag-and-drop still available)
 *
 * Route: /prioritization/open-questions
 */

import { useEffect, useState } from 'react';
import {
  HelpCircle, Plus, Check, Clock, XCircle, RotateCcw,
  Loader2, AlertCircle, Link2, Target, BookOpen, X,
  GripVertical, ChevronDown, ChevronUp, LayoutList, Table2,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from './strategy/StrategyConnectivityDocModal';
import { EntitySyncPanel } from '../../components/ui/EntitySyncPanel';
import {
  useOpenQuestionsStore,
  STATUS_TRANSITIONS,
  type OpenQuestion,
  type OpenQuestionStatus,
  type CreateQuestionInput,
} from '../../stores/prioritization/openQuestionsStore';
import { LinkToItemPanel } from '../../components/methodology/LinkToItemPanel';
import { getItemCountState } from '../../components/collection/types';

// ── Constants ─────────────────────────────────────────────────

// ── Status helpers ────────────────────────────────────────────

const STATUS_LABELS: Record<OpenQuestionStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Answered',
  deferred: 'Deferred',
};

const STATUS_DOT_COLORS: Record<OpenQuestionStatus, string> = {
  open: 'bg-[var(--slate)]',
  in_progress: 'bg-[var(--mustard)]',
  resolved: 'bg-[var(--green)]',
  deferred: 'bg-[var(--surface)]',
};

const STATUS_TEXT_COLORS: Record<OpenQuestionStatus, string> = {
  open: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
  in_progress: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
  resolved: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  deferred: 'text-[var(--text-ter)]',
};

function StatusIndicator({ status }: { status: OpenQuestionStatus }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium select-none ${STATUS_TEXT_COLORS[status]}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT_COLORS[status]}`} />
      {STATUS_LABELS[status]}
    </span>
  );
}

// ── Framework field (auto-save on blur) ───────────────────────

interface FieldProps {
  label: string;
  value: string | null;
  placeholder: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  accent?: string;
}

function Field({ label, value, placeholder, onChange, onBlur, accent = 'border-[var(--rule)]' }: FieldProps) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide mb-1 text-[var(--text-ter)]">{label}</p>
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        rows={2}
        className={`w-full px-3 py-2 text-sm border ${accent}  bg-[var(--surface)] text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
      />
    </div>
  );
}

// ── Card view row (sortable) ──────────────────────────────────

interface CardRowProps {
  question: OpenQuestion;
  rank: number;
}

function SortableCardRow({ question, rank }: CardRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question.id });
  const { transitionStatus, deleteQuestion, updateQuestion } = useOpenQuestionsStore();

  const [isExpanded, setIsExpanded] = useState(question.priority === 1);
  const [showResolveInput, setShowResolveInput] = useState(false);
  const [isActing, setIsActing] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const [draft, setDraft] = useState({
    text: question.text,
    currentTheory: question.currentTheory ?? '',
    sourceOfAnswer: question.sourceOfAnswer ?? '',
    stepsToAnswer: question.stepsToAnswer ?? '',
    resolutionText: question.resolutionText ?? '',
  });

  useEffect(() => {
    setDraft({
      text: question.text,
      currentTheory: question.currentTheory ?? '',
      sourceOfAnswer: question.sourceOfAnswer ?? '',
      stepsToAnswer: question.stepsToAnswer ?? '',
      resolutionText: question.resolutionText ?? '',
    });
  }, [question.text, question.currentTheory, question.sourceOfAnswer, question.stepsToAnswer, question.resolutionText]);

  async function save(field: keyof typeof draft) {
    const val = draft[field] || null;
    await updateQuestion(question.id, { [field]: val } as Parameters<typeof updateQuestion>[1]);
  }

  async function handleTransition(newStatus: OpenQuestionStatus) {
    if (newStatus === 'resolved') {
      if (draft.resolutionText.trim()) {
        setIsActing(true);
        await transitionStatus(question.id, 'resolved', draft.resolutionText.trim());
        setIsActing(false);
      } else {
        setShowResolveInput(true);
      }
      return;
    }
    setIsActing(true);
    await transitionStatus(question.id, newStatus);
    setIsActing(false);
  }

  async function handleDelete() {
    if (!confirm('Delete this question?')) return;
    await deleteQuestion(question.id);
  }

  const allowed = STATUS_TRANSITIONS[question.status];
  const isFocusing = rank === 1;
  const isAnswered = question.status === 'resolved';

  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={` border transition-all
        ${isDragging ? 'opacity-50 shadow-lg scale-[1.01]' : ''}
        ${isFocusing ? 'border-[var(--slate)] dark:border-[var(--slate)] ring-1 ring-blue-300 dark:ring-blue-800' : isAnswered ? 'border-[var(--green)] dark:border-[var(--green)] opacity-70' : 'border-[var(--rule)]'}
        bg-[var(--bg-raised)] dark:bg-[var(--surface)]`}
    >
      {isFocusing && (
        <div className="flex items-center gap-2 px-4 py-1.5 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border-b border-[var(--slate)] dark:border-[var(--slate)]">
          <Target className="h-3 w-3 text-[var(--text-ter)]" />
          <span className="text-[10px] font-semibold text-[var(--text-sec)] uppercase tracking-wide">Focusing Question</span>
        </div>
      )}

      <div className="flex items-start gap-2 p-3">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 mt-1 p-1 text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] cursor-grab active:cursor-grabbing transition-colors"
          tabIndex={-1}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Rank */}
        <span className={`flex-shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold
          ${isFocusing ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'}`}>
          {rank}
        </span>

        {/* Question text */}
        <div className="flex-1 min-w-0">
          <textarea
            value={draft.text}
            onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
            onBlur={() => save('text')}
            rows={2}
            className={`w-full text-sm font-medium bg-transparent resize-none focus:outline-none border-0 p-0
              ${isAnswered ? 'text-[var(--text-ter)] line-through' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}
          />
          {isAnswered && question.resolutionText && !isExpanded && (
            <button onClick={() => setIsExpanded(true)}
              className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] hover:underline text-left">
              Answer: {question.resolutionText.length > 70 ? question.resolutionText.slice(0, 70) + '…' : question.resolutionText}
            </button>
          )}
        </div>

        {/* Status + expand */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusIndicator status={question.status} />
          <button onClick={() => setIsExpanded((v) => !v)}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors">
            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Framework fields */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 space-y-3 border-t border-[var(--rule)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Current Theory" value={draft.currentTheory}
              placeholder="What do we think the answer is right now?"
              onChange={(v) => setDraft((d) => ({ ...d, currentTheory: v }))}
              onBlur={() => save('currentTheory')} accent="border-[var(--slate)] dark:border-[var(--slate)]" />
            <Field label="Source of Answer" value={draft.sourceOfAnswer}
              placeholder="Who has a definitive answer? Where to look?"
              onChange={(v) => setDraft((d) => ({ ...d, sourceOfAnswer: v }))}
              onBlur={() => save('sourceOfAnswer')} accent="border-[var(--mustard)] dark:border-[var(--mustard)]" />
            <Field label="Source of a Better Answer" value={draft.stepsToAnswer}
              placeholder="Who can inform a better answer?"
              onChange={(v) => setDraft((d) => ({ ...d, stepsToAnswer: v }))}
              onBlur={() => save('stepsToAnswer')} accent="border-[var(--slate)] dark:border-[var(--slate)]" />
            <Field label="Actual Answer" value={draft.resolutionText}
              placeholder="What does the real answer turn out to be?"
              onChange={(v) => setDraft((d) => ({ ...d, resolutionText: v }))}
              onBlur={() => save('resolutionText')} accent="border-[var(--green)] dark:border-[var(--green)]" />
          </div>

          {showResolveInput && (
            <div className="p-3 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)]">
              <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mb-2">Fill in the Actual Answer above, then click Mark Answered.</p>
              <button onClick={() => handleTransition('resolved')} disabled={!draft.resolutionText.trim() || isActing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--green)] hover:bg-[var(--green)] disabled:opacity-60 text-white text-xs font-medium transition-colors">
                {isActing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Mark Answered
              </button>
            </div>
          )}

          {showLink && (
            <LinkToItemPanel sourceEntityType="open_question" sourceEntityId={question.id}
              onLinked={() => setShowLink(false)} onClose={() => setShowLink(false)} />
          )}

          {!showResolveInput && (
            <div className="flex items-center gap-3 pt-1 flex-wrap">
              {allowed.includes('in_progress') && question.status === 'open' && (
                <button onClick={() => handleTransition('in_progress')} disabled={isActing}
                  className="flex items-center gap-1 text-xs text-[var(--mustard)] hover:text-[var(--mustard)] disabled:opacity-60">
                  <Clock className="h-3 w-3" /> In Progress
                </button>
              )}
              {allowed.includes('resolved') && (
                <button onClick={() => handleTransition('resolved')} disabled={isActing}
                  className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] disabled:opacity-60">
                  <Check className="h-3 w-3" /> Mark Answered
                </button>
              )}
              {allowed.includes('deferred') && (
                <button onClick={() => handleTransition('deferred')} disabled={isActing}
                  className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:opacity-60">
                  <XCircle className="h-3 w-3" /> Defer
                </button>
              )}
              {allowed.includes('open') && question.status !== 'open' && (
                <button onClick={() => handleTransition('open')} disabled={isActing}
                  className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] disabled:opacity-60">
                  <RotateCcw className="h-3 w-3" /> Reopen
                </button>
              )}
              <button onClick={() => setShowLink((v) => !v)}
                className={`flex items-center gap-1 text-xs transition-colors ${showLink ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)] hover:text-[var(--text-ter)]'}`}>
                <Link2 className="h-3 w-3" /> Link to item
              </button>
              <button onClick={handleDelete}
                className="ml-auto text-xs text-[var(--text-ter)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] transition-colors">
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Table view row (sortable) ─────────────────────────────────

function SortableTableRow({ question, rank }: CardRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question.id });
  const { transitionStatus, deleteQuestion, updateQuestion } = useOpenQuestionsStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isActing, setIsActing] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [draft, setDraft] = useState({
    text: question.text,
    currentTheory: question.currentTheory ?? '',
    sourceOfAnswer: question.sourceOfAnswer ?? '',
    stepsToAnswer: question.stepsToAnswer ?? '',
    resolutionText: question.resolutionText ?? '',
  });

  useEffect(() => {
    setDraft({
      text: question.text,
      currentTheory: question.currentTheory ?? '',
      sourceOfAnswer: question.sourceOfAnswer ?? '',
      stepsToAnswer: question.stepsToAnswer ?? '',
      resolutionText: question.resolutionText ?? '',
    });
  }, [question.text, question.currentTheory, question.sourceOfAnswer, question.stepsToAnswer, question.resolutionText]);

  async function save(field: keyof typeof draft) {
    const val = draft[field] || null;
    await updateQuestion(question.id, { [field]: val } as Parameters<typeof updateQuestion>[1]);
  }

  async function handleTransition(newStatus: OpenQuestionStatus) {
    if (newStatus === 'resolved' && !draft.resolutionText.trim()) return;
    setIsActing(true);
    await transitionStatus(question.id, newStatus === 'resolved' ? newStatus : newStatus, newStatus === 'resolved' ? draft.resolutionText.trim() : undefined);
    setIsActing(false);
  }

  async function handleDelete() {
    if (!confirm('Delete this question?')) return;
    await deleteQuestion(question.id);
  }

  const allowed = STATUS_TRANSITIONS[question.status];
  const isFocusing = rank === 1;
  const isAnswered = question.status === 'resolved';

  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <>
      <tr
        ref={setNodeRef}
        style={style}
        className={`border-b border-[var(--rule)] transition-colors
          ${isDragging ? 'opacity-50 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/10' : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/40'}
          ${isFocusing ? 'bg-[var(--slate-dim)]/50 dark:bg-[var(--slate)]/10' : ''}`}
      >
        {/* Drag handle */}
        <td className="w-8 pl-2">
          <button {...attributes} {...listeners}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] cursor-grab active:cursor-grabbing"
            tabIndex={-1}>
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        </td>

        {/* Rank */}
        <td className="w-8 text-center">
          <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold
            ${isFocusing ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'}`}>
            {rank}
          </span>
        </td>

        {/* Question */}
        <td className="px-3 py-2.5 max-w-0">
          <div className="flex items-start gap-2">
            {isFocusing && <Target className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />}
            <p className={`text-sm truncate ${isAnswered ? 'line-through text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
              {question.text}
            </p>
          </div>
        </td>

        {/* Filled indicators */}
        <td className="px-2 w-24">
          <div className="flex items-center gap-1">
            {(['currentTheory', 'sourceOfAnswer', 'stepsToAnswer', 'resolutionText'] as const).map((f, i) => (
              <span key={f} title={['Theory', 'Source', 'Better Source', 'Answer'][i]}
                className={`w-2 h-2 rounded-full ${question[f] ? ['bg-[var(--slate)]', 'bg-[var(--mustard)]', 'bg-[var(--slate)]', 'bg-[var(--green)]'][i] : 'bg-[var(--hover-tint)]'}`} />
            ))}
          </div>
        </td>

        {/* Status */}
        <td className="px-3 w-28">
          <StatusIndicator status={question.status} />
        </td>

        {/* Expand */}
        <td className="w-10 pr-2 text-right">
          <button onClick={() => setIsExpanded((v) => !v)}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors">
            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </td>
      </tr>

      {/* Expanded detail row */}
      {isExpanded && (
        <tr className="border-b border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/30">
          <td colSpan={6} className="px-10 py-4">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Field label="Current Theory" value={draft.currentTheory}
                placeholder="What do we think the answer is?" onChange={(v) => setDraft((d) => ({ ...d, currentTheory: v }))}
                onBlur={() => save('currentTheory')} accent="border-[var(--slate)] dark:border-[var(--slate)]" />
              <Field label="Source of Answer" value={draft.sourceOfAnswer}
                placeholder="Who has the answer?" onChange={(v) => setDraft((d) => ({ ...d, sourceOfAnswer: v }))}
                onBlur={() => save('sourceOfAnswer')} accent="border-[var(--mustard)] dark:border-[var(--mustard)]" />
              <Field label="Source of a Better Answer" value={draft.stepsToAnswer}
                placeholder="Who can inform a better answer?" onChange={(v) => setDraft((d) => ({ ...d, stepsToAnswer: v }))}
                onBlur={() => save('stepsToAnswer')} accent="border-[var(--slate)] dark:border-[var(--slate)]" />
              <Field label="Actual Answer" value={draft.resolutionText}
                placeholder="What does the real answer turn out to be?" onChange={(v) => setDraft((d) => ({ ...d, resolutionText: v }))}
                onBlur={() => save('resolutionText')} accent="border-[var(--green)] dark:border-[var(--green)]" />
            </div>
            {showLink && (
              <div className="mb-3">
                <LinkToItemPanel sourceEntityType="open_question" sourceEntityId={question.id}
                  onLinked={() => setShowLink(false)} onClose={() => setShowLink(false)} />
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              {allowed.includes('in_progress') && question.status === 'open' && (
                <button onClick={() => handleTransition('in_progress')} disabled={isActing}
                  className="flex items-center gap-1 text-xs text-[var(--mustard)] hover:text-[var(--mustard)] disabled:opacity-60">
                  <Clock className="h-3 w-3" /> In Progress
                </button>
              )}
              {allowed.includes('resolved') && (
                <button onClick={() => handleTransition('resolved')} disabled={isActing || !draft.resolutionText.trim()}
                  className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] disabled:opacity-60"
                  title={!draft.resolutionText.trim() ? 'Fill in Actual Answer first' : undefined}>
                  <Check className="h-3 w-3" /> Mark Answered
                </button>
              )}
              {allowed.includes('deferred') && (
                <button onClick={() => handleTransition('deferred')} disabled={isActing}
                  className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:opacity-60">
                  <XCircle className="h-3 w-3" /> Defer
                </button>
              )}
              {allowed.includes('open') && question.status !== 'open' && (
                <button onClick={() => handleTransition('open')} disabled={isActing}
                  className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] disabled:opacity-60">
                  <RotateCcw className="h-3 w-3" /> Reopen
                </button>
              )}
              <button onClick={() => setShowLink((v) => !v)}
                className={`flex items-center gap-1 text-xs transition-colors ${showLink ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)] hover:text-[var(--text-ter)]'}`}>
                <Link2 className="h-3 w-3" /> Link to item
              </button>
              <button onClick={handleDelete}
                className="ml-auto text-xs text-[var(--text-ter)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] transition-colors">
                Delete
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Add question form ─────────────────────────────────────────

function AddQuestionForm({ onClose, nextPriority }: { onClose: () => void; nextPriority: number }) {
  const { createQuestion } = useOpenQuestionsStore();
  const [form, setForm] = useState<CreateQuestionInput>({ text: '', currentTheory: '', sourceOfAnswer: '', stepsToAnswer: '' });
  const [isAdding, setIsAdding] = useState(false);

  async function handleAdd() {
    if (!form.text.trim()) return;
    setIsAdding(true);
    const { error } = await createQuestion({
      ...form, text: form.text.trim(), priority: nextPriority,
      currentTheory: form.currentTheory || null,
      sourceOfAnswer: form.sourceOfAnswer || null,
      stepsToAnswer: form.stepsToAnswer || null,
    });
    setIsAdding(false);
    if (error) { alert(`Failed to add question: ${error}`); return; }
    onClose();
  }

  return (
    <div className="p-4 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/10 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-[var(--text-sec)]">New Question — Priority #{nextPriority}</p>
        <button onClick={onClose} className="text-[var(--text-sec)] hover:text-[var(--text-sec)]"><X className="h-4 w-4" /></button>
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-sec)] mb-1">Question *</p>
        <textarea autoFocus value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAdd(); if (e.key === 'Escape') onClose(); }}
          placeholder="What needs to be answered or clarified?" rows={2}
          className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(['currentTheory', 'sourceOfAnswer', 'stepsToAnswer'] as const).map((field, i) => (
          <div key={field}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-sec)] mb-1">
              {['Current Theory', 'Source of Answer', 'Source of a Better Answer'][i]}
            </p>
            <textarea value={(form[field] as string) ?? ''} onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
              placeholder={['What do we think now?', 'Who has the answer?', 'Who can inform a better answer?'][i]} rows={2}
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={handleAdd} disabled={!form.text.trim() || isAdding}
          className="flex items-center gap-1.5 px-3 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-60 text-white text-sm font-medium transition-colors">
          {isAdding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Add Question
        </button>
        <button onClick={onClose} className="px-3 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-sec)] transition-colors">Cancel</button>
        <span className="ml-auto text-xs text-[var(--text-sec)] self-center">⌘↵ to add</span>
      </div>
    </div>
  );
}

// ── Framework guide ───────────────────────────────────────────

function FrameworkGuide({ onClose }: { onClose: () => void }) {
  return (
    <div className="p-4 border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg)]/40 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-[var(--text-ter)]" />
          <p className="text-sm font-semibold text-[var(--text-sec)]">The Open Questions Framework</p>
        </div>
        <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"><X className="h-4 w-4" /></button>
      </div>
      <p className="text-xs text-[var(--text-ter)] leading-relaxed">
        From <em>Managing Priorities</em> by Harry Max. "The quality of your questions determines the impact of the answers."
        Rank questions by importance, then answer them in that order. The #1 question is your Focusing Question.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { n: '1', label: 'Priority', desc: 'Stack rank — #1 is your Focusing Question', dot: 'bg-[var(--slate)]' },
          { n: '2', label: 'Question', desc: 'Specific, insightful, penetrating', dot: 'bg-[var(--surface)]' },
          { n: '3', label: 'Current Theory', desc: 'What do we think the answer is now?', dot: 'bg-[var(--slate)]' },
          { n: '4', label: 'Source of Answer', desc: 'Who has the definitive answer?', dot: 'bg-[var(--mustard)]' },
          { n: '5', label: 'Source of a Better Answer', desc: 'Who can inform a better answer?', dot: 'bg-[var(--slate)]' },
        ].map((col) => (
          <div key={col.n} className="p-2 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)]">
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`w-2 h-2 rounded-full ${col.dot}`} />
              <p className="text-xs font-semibold text-[var(--text-sec)]">{col.label}</p>
            </div>
            <p className="text-[11px] text-[var(--text-ter)]">{col.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────

type ViewMode = 'cards' | 'table';
type FilterStatus = 'all' | OpenQuestionStatus;

export default function OpenQuestionsPage() {
  const { questions, syncState, fetchFromSupabase, reprioritize, createQuestion } = useOpenQuestionsStore();
  const [showForm, setShowForm] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [viewMode, setViewMode] = useState<ViewMode | null>(null); // null = auto

  useEffect(() => { fetchFromSupabase(); }, [fetchFromSupabase]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Sort: ranked first (asc), then unranked by created_at desc
  const sorted = [...questions].sort((a, b) => {
    if (a.priority !== null && b.priority !== null) return a.priority - b.priority;
    if (a.priority !== null) return -1;
    if (b.priority !== null) return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const filtered = filterStatus === 'all' ? sorted : sorted.filter((q) => q.status === filterStatus);

  const countState = getItemCountState(filtered.length);
  // Auto-select view: many (8+) → table, otherwise → cards. User can override.
  const effectiveView: ViewMode = viewMode ?? (countState === 'many' ? 'table' : 'cards');

  const counts = questions.reduce(
    (acc, q) => ({ ...acc, [q.status]: (acc[q.status] ?? 0) + 1 }),
    {} as Record<OpenQuestionStatus, number>
  );

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = filtered.findIndex((q) => q.id === active.id);
    const newIdx = filtered.findIndex((q) => q.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = [...filtered];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    await reprioritize(reordered.map((q) => q.id));
  }

  const filterOptions: { value: FilterStatus; label: string }[] = [
    { value: 'all', label: `All (${questions.length})` },
    { value: 'open', label: `Open (${counts.open ?? 0})` },
    { value: 'in_progress', label: `In Progress (${counts.in_progress ?? 0})` },
    { value: 'resolved', label: `Answered (${counts.resolved ?? 0})` },
    { value: 'deferred', label: `Deferred (${counts.deferred ?? 0})` },
  ];

  const nextPriority = sorted.filter((q) => q.priority !== null).length + 1;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={<HelpCircle className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30"
        title="Open Questions"
        subtitle="Stack-ranked questions that must be answered to move forward. #1 is the Focusing Question."
        actions={
          <div className="flex items-center gap-2">
            <StrategyConnectivityHelpButton context="open_questions" />
            <button onClick={() => setShowGuide((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors">
              <BookOpen className="h-4 w-4" /> Guide
            </button>
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors">
              <Plus className="h-4 w-4" /> Add Question
            </button>
          </div>
        }
      />

      <EntitySyncPanel
        entityType="open_questions"
        data={questions.map((q) => ({
          text: q.text,
          status: q.status,
          context_entity_type: q.contextEntityType ?? '',
          context_entity_id: q.contextEntityId ?? '',
          resolution_text: q.resolutionText ?? '',
        }))}
        filename="open-questions"
        onImport={async (rows) => {
          // Return created identities so the panel registers external identity
          // + a change event for connector-sourced rows (set 5B convergence).
          const refs: { externalId: string; internalId: string; externalUrl?: string; label?: string }[] = [];
          for (const row of rows) {
            const { question } = await createQuestion({
              text: String(row.text ?? ''),
              currentTheory: row.currentTheory ? String(row.currentTheory) : null,
              sourceOfAnswer: row.sourceOfAnswer ? String(row.sourceOfAnswer) : null,
              stepsToAnswer: row.stepsToAnswer ? String(row.stepsToAnswer) : null,
              resolutionText: row.resolutionText ? String(row.resolutionText) : null,
            });
            if (question?.id && row.external_id) {
              refs.push({
                externalId: String(row.external_id),
                internalId: question.id,
                ...(row.external_url ? { externalUrl: String(row.external_url) } : {}),
                label: String(row.text ?? ''),
              });
            }
          }
          return refs;
        }}
      />

      {showGuide && <FrameworkGuide onClose={() => setShowGuide(false)} />}
      {showForm && <AddQuestionForm onClose={() => setShowForm(false)} nextPriority={nextPriority} />}

      {/* Toolbar: filters + view toggle */}
      <div className="flex items-center gap-1 flex-wrap">
        {filterOptions.map((opt) => (
          <button key={opt.value} onClick={() => setFilterStatus(opt.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors
              ${filterStatus === opt.value ? 'bg-[var(--slate)] text-white' : 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)]'}`}>
            {opt.label}
          </button>
        ))}

        {/* View toggle — only show when there are items */}
        {filtered.length > 0 && (
          <div className="ml-auto flex items-center gap-1 border border-[var(--rule)] overflow-hidden">
            <button onClick={() => setViewMode('cards')}
              title="Card view"
              className={`p-1.5 transition-colors ${effectiveView === 'cards' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}>
              <LayoutList className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setViewMode('table')}
              title="Table view"
              className={`p-1.5 transition-colors ${effectiveView === 'table' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}>
              <Table2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Loading */}
      {syncState.isLoading && (
        <div className="flex items-center justify-center py-10 text-[var(--text-ter)]">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…
        </div>
      )}

      {/* Error */}
      {!syncState.isLoading && syncState.syncError && (
        <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          <AlertCircle className="h-4 w-4 flex-shrink-0" /> {syncState.syncError}
          <button onClick={fetchFromSupabase} className="ml-auto underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Empty */}
      {!syncState.isLoading && !syncState.syncError && filtered.length === 0 && (
        <div className="text-center py-12 border border-dashed border-[var(--rule)]">
          <HelpCircle className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {filterStatus === 'all' ? 'No open questions yet. Add one to start building clarity.' : `No questions with status "${STATUS_LABELS[filterStatus as OpenQuestionStatus]}".`}
          </p>
          {filterStatus === 'all' && (
            <button onClick={() => setShowForm(true)}
              className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors">
              <Plus className="h-4 w-4" /> Add your first question
            </button>
          )}
        </div>
      )}

      {/* DnD context wraps both views */}
      {!syncState.isLoading && filtered.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filtered.map((q) => q.id)} strategy={verticalListSortingStrategy}>

            {/* ── Card view ── */}
            {effectiveView === 'cards' && (
              <div className="flex flex-col gap-2">
                {filtered.map((q, idx) => (
                  <SortableCardRow key={q.id} question={q} rank={idx + 1} />
                ))}
              </div>
            )}

            {/* ── Table view ── */}
            {effectiveView === 'table' && (
              <div className="border border-[var(--rule)] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[var(--surface)] dark:bg-[var(--bg-raised)]/60 border-b border-[var(--rule)]">
                      <th className="w-8" />
                      <th className="w-8 text-center py-2">
                        <span className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">#</span>
                      </th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Question</th>
                      <th className="px-2 py-2 w-24">
                        <span className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide" title="Theory · Source · Steps · Answer">Fields</span>
                      </th>
                      <th className="px-3 py-2 w-28 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Status</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((q, idx) => (
                      <SortableTableRow key={q.id} question={q} rank={idx + 1} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
